'use client';

import { STATUS, SURFACE, BORDER } from '@/constants/colors';
import { memo } from 'react';
import Image from 'next/image';
import {
  Box,
  Stack,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  alpha,
} from '@mui/material';
import {
  Home,
  History,
  LocalShipping,
  AccountCircle,
  HelpOutline,
  Logout,
  Menu,
  ChevronLeft,
  Dashboard,
  LocationOn,
  BarChart,
  AccountBalanceWallet,
  Receipt,
  Gavel,
  Inventory2,
} from '@mui/icons-material';
import Link from 'next/link';
import { themeColors } from '../theme/theme';
import { brandingConfig } from '@/config/branding';

const DESKTOP_SIDEBAR_WIDTH = 240;
const DESKTOP_SIDEBAR_COLLAPSED = 68;

/**
 * Desktop Sidebar Component - Collapsible sidebar for desktop layouts
 */
const DesktopSidebar = memo(
  ({ user, isExpanded = true, onToggleExpanded, onLogout, activeKey = '' }) => {
    const isBusiness = user?.userType?.toLowerCase() === 'business';
    const sidebarPrimaryColor = isBusiness ? '#f59e0b' : themeColors.primaryColor;

    // Build navigation items based on user type AND business setup status
    let mainNavItems = [];

    if (isBusiness) {
      if (user?.businessSetupCompleted) {
        // Complete business profile - show all items
        mainNavItems = [
          {
            key: 'profile',
            label: 'Profile',
            icon: AccountCircle,
            path: '/profile',
          },
          {
            key: 'business-dashboard',
            label: 'Dashboard',
            icon: Dashboard,
            path: '/business-dashboard',
          },
          {
            key: 'my-pricing',
            label: 'My Pricing',
            icon: Receipt,
            path: '/my-pricing',
          },
          {
            key: 'bulk-booking',
            label: 'Bulk Booking',
            icon: Inventory2,
            path: '/bulk-booking',
          },
          {
            key: 'saved-addresses',
            label: 'Addresses',
            icon: LocationOn,
            path: '/saved-addresses',
          },
          {
            key: 'activeParcels',
            label: 'Active',
            icon: LocalShipping,
            path: '/activeParcels',
          },
          { key: 'history', label: 'History', icon: History, path: '/history' },
          {
            key: 'invoices',
            label: 'Invoices',
            icon: Receipt,
            path: '/invoices',
          },
          {
            key: 'analytics',
            label: 'Analytics',
            icon: BarChart,
            path: '/business-analytics',
          },
        ];
      } else {
        // Incomplete business profile - ONLY show profile
        mainNavItems = [
          {
            key: 'profile',
            label: 'Profile',
            icon: AccountCircle,
            path: '/profile',
          },
        ];
      }
    } else {
      // Individual users - show all items
      mainNavItems = [
        {
          key: 'profile',
          label: 'Profile',
          icon: AccountCircle,
          path: '/profile',
        },
        { key: 'home', label: 'Home', icon: Home, path: '/home' },
        {
          key: 'activeParcels',
          label: 'Active',
          icon: LocalShipping,
          path: '/activeParcels',
        },
        { key: 'history', label: 'History', icon: History, path: '/history' },
        {
          key: 'invoices',
          label: 'Invoices',
          icon: Receipt,
          path: '/invoices',
        },
      ];
    }

    const bottomNavItems = [
      { key: 'support', label: 'Support', icon: HelpOutline, path: '/support' },
      {
        key: 'terms',
        label: 'Terms & Conditions',
        icon: Gavel,
        path: '/terms',
      },
    ];

    // Only add wallet for:
    // 1. Individual users (always)
    // 2. Business users with completed profiles
    const showWallet = !isBusiness || user?.businessSetupCompleted;
    if (showWallet) {
      bottomNavItems.unshift({
        key: 'wallet',
        label: 'Wallet',
        icon: AccountBalanceWallet,
        path: '/wallet',
      });
    }

    const isActive = (key) => {
      if (activeKey === key) return true;
      if (key === 'business-dashboard' && activeKey === 'home') return true;
      return false;
    };

    return (
      <Box
        sx={{
          width: isExpanded ? DESKTOP_SIDEBAR_WIDTH : DESKTOP_SIDEBAR_COLLAPSED,
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bgcolor: '#fff',
          borderRight: '1px solid #e5e7eb',
          transition: 'width 0.25s ease',
          zIndex: 1200,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={isExpanded ? 'space-between' : 'center'}
          sx={{ p: 1.5, borderBottom: '1px solid #f3f4f6', minHeight: 56 }}
        >
          {isExpanded && (
            <Box
              sx={{
                height: 32,
                width: 130,
                position: 'relative',
                cursor: 'pointer',
              }}
              onClick={() => (window.location.href = '/')}
            >
              <Image
                src="/assets/terrakite-teal-logo.png"
                alt={brandingConfig.assets.logoAlt}
                fill
                style={{ objectFit: 'contain', objectPosition: 'left' }}
                priority
              />
            </Box>
          )}
          <IconButton
            onClick={onToggleExpanded}
            size="small"
            sx={{ color: '#6b7280' }}
            aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {isExpanded ? <ChevronLeft fontSize="small" /> : <Menu fontSize="small" />}
          </IconButton>
        </Stack>

        {/* User Info */}
        {user && isExpanded && (
          <Box sx={{ p: 1.5, borderBottom: '1px solid #f3f4f6' }}>
            <Link href="/profile" passHref legacyBehavior>
              <Stack
                component="a"
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  borderRadius: 2,
                  p: 1,
                  mx: -1,
                  '&:hover': { bgcolor: '#f9fafb' },
                }}
              >
                <Avatar
                  src={user.avatar}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: sidebarPrimaryColor,
                    fontSize: '0.9rem',
                  }}
                >
                  {user.userName?.[0] || 'U'}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: '#1f2937',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {user.userName || 'User'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                    {isBusiness ? 'Business' : 'Individual'}
                  </Typography>
                </Box>
              </Stack>
            </Link>
          </Box>
        )}

        {/* New Shipment Button */}
        {isExpanded && (
          <Box sx={{ p: 1.5 }}>
            <Link href="/booking" passHref legacyBehavior>
              <Box
                component="a"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  py: 1.5,
                  px: 2,
                  borderRadius: 2.5,
                  background: `linear-gradient(135deg, ${sidebarPrimaryColor} 0%, ${isBusiness ? '#d97706' : '#059669'} 100%)`,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: `0 4px 15px ${alpha(sidebarPrimaryColor, 0.4)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px ${alpha(sidebarPrimaryColor, 0.5)}`,
                  },
                }}
              >
                <LocalShipping sx={{ fontSize: 20 }} />
                New Shipment
              </Box>
            </Link>
          </Box>
        )}

        {/* Main Navigation */}
        <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
          <List disablePadding>
            {mainNavItems.map((item) => {
              const active = isActive(item.key);
              const isBulkBooking = item.key === 'bulk-booking';
              const bulkBookingPurple = '#8b5cf6';

              return (
                <ListItem key={item.key} disablePadding sx={{ px: 0.75 }}>
                  <Link href={item.path} passHref legacyBehavior prefetch>
                    <ListItemButton
                      component="a"
                      sx={{
                        borderRadius: 2,
                        mb: 0.25,
                        py: 1,
                        px: isExpanded ? 1.5 : 1,
                        minHeight: 44,
                        justifyContent: isExpanded ? 'flex-start' : 'center',
                        bgcolor: active ? alpha(sidebarPrimaryColor, 0.15) : 'transparent',
                        borderLeft: active
                          ? `3px solid ${sidebarPrimaryColor}`
                          : '3px solid transparent',
                        position: 'relative',
                        ...(isBulkBooking &&
                          !active && {
                            background: `linear-gradient(135deg, ${alpha(bulkBookingPurple, 0.12)} 0%, ${alpha(bulkBookingPurple, 0.06)} 100%)`,
                            border: `2px solid ${alpha(bulkBookingPurple, 0.25)}`,
                            boxShadow: `0 0 12px ${alpha(bulkBookingPurple, 0.25)}`,
                            '&::after': {
                              content: '"PRO"',
                              position: 'absolute',
                              top: -6,
                              right: isExpanded ? 8 : -6,
                              fontSize: '0.6rem',
                              fontWeight: 900,
                              color: '#fff',
                              bgcolor: bulkBookingPurple,
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 1,
                              boxShadow: `0 2px 4px ${alpha(bulkBookingPurple, 0.3)}`,
                            },
                          }),
                        '&:hover': {
                          bgcolor: active ? alpha(sidebarPrimaryColor, 0.2) : alpha('#000', 0.04),
                          ...(isBulkBooking && {
                            boxShadow: `0 0 16px ${alpha(sidebarPrimaryColor, 0.35)}`,
                          }),
                        },
                        textDecoration: 'none',
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: isExpanded ? 36 : 'auto',
                          color: active
                            ? sidebarPrimaryColor
                            : isBulkBooking && !active
                              ? bulkBookingPurple
                              : '#6b7280',
                        }}
                      >
                        <item.icon sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      {isExpanded && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: active ? 700 : isBulkBooking && !active ? 700 : 500,
                            color: active
                              ? sidebarPrimaryColor
                              : isBulkBooking && !active
                                ? bulkBookingPurple
                                : '#374151',
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Link>
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ my: 1 }} />

          <List disablePadding>
            {bottomNavItems.map((item) => (
              <ListItem key={item.key} disablePadding sx={{ px: 0.75 }}>
                <Link href={item.path} passHref legacyBehavior prefetch>
                  <ListItemButton
                    component="a"
                    sx={{
                      borderRadius: 2,
                      mb: 0.25,
                      py: 0.75,
                      px: isExpanded ? 1.5 : 1,
                      minHeight: 40,
                      justifyContent: isExpanded ? 'flex-start' : 'center',
                      bgcolor: isActive(item.key)
                        ? alpha(sidebarPrimaryColor, 0.15)
                        : 'transparent',
                      borderLeft: isActive(item.key)
                        ? `3px solid ${sidebarPrimaryColor}`
                        : '3px solid transparent',
                      '&:hover': {
                        bgcolor: isActive(item.key)
                          ? alpha(sidebarPrimaryColor, 0.2)
                          : alpha('#000', 0.04),
                      },
                      textDecoration: 'none',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: isExpanded ? 36 : 'auto',
                        color: isActive(item.key) ? sidebarPrimaryColor : '#6b7280',
                      }}
                    >
                      <item.icon sx={{ fontSize: 18 }} />
                    </ListItemIcon>
                    {isExpanded && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.8rem',
                          fontWeight: isActive(item.key) ? 600 : 500,
                          color: isActive(item.key) ? sidebarPrimaryColor : '#4b5563',
                        }}
                      />
                    )}
                  </ListItemButton>
                </Link>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Logout */}
        <Box sx={{ p: 0.75, borderTop: '1px solid #f3f4f6' }}>
          <ListItemButton
            onClick={onLogout}
            sx={{
              borderRadius: 2,
              py: 1,
              px: isExpanded ? 1.5 : 1,
              justifyContent: isExpanded ? 'flex-start' : 'center',
              color: '#dc2626',
              '&:hover': { bgcolor: alpha('#dc2626', 0.08) },
            }}
          >
            <ListItemIcon sx={{ minWidth: isExpanded ? 36 : 'auto', color: '#dc2626' }}>
              <Logout sx={{ fontSize: 18 }} />
            </ListItemIcon>
            {isExpanded && (
              <ListItemText
                primary="Sign Out"
                primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 600 }}
              />
            )}
          </ListItemButton>
        </Box>
      </Box>
    );
  }
);

DesktopSidebar.displayName = 'DesktopSidebar';

export { DESKTOP_SIDEBAR_WIDTH, DESKTOP_SIDEBAR_COLLAPSED };
export default DesktopSidebar;
